/**
 * API Client for AstroLucy backend
 *
 * All API calls go through this module for consistent error handling
 * and request/response processing.
 */

import { useErrorStore, getErrorMessage, type ErrorType } from '../stores/errorStore';
import i18n from '../i18n';

const API_BASE = '';  // Same origin, so no base needed
const DEFAULT_TIMEOUT = 30000;  // 30 seconds
const STREAM_TIMEOUT = 180000;  // 3 minutes for streaming (LLM can be slow)

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  status: number;
  errorType: ErrorType;
  data?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    errorType: ErrorType = 'unknown',
    data?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorType = errorType;
    this.data = data;
  }
}

/**
 * Determine error type from status code or error
 */
function getErrorType(status: number): ErrorType {
  if (status === 401) return 'auth';
  if (status === 408 || status === 504) return 'timeout';
  if (status >= 500) return 'server';
  if (status >= 400) return 'client';
  return 'unknown';
}

/**
 * Handle errors globally - show toast and optionally redirect
 */
function handleGlobalError(error: ApiError, showToast = true): void {
  const { addError } = useErrorStore.getState();

  if (error.errorType === 'auth') {
    // Session expired - show error with login action
    addError({
      type: 'auth',
      message: getErrorMessage('auth'),
      dismissable: false,
      action: {
        label: i18n.t('auth.login'),
        handler: () => {
          window.location.href = '/login';
        },
      },
    });
  } else if (showToast) {
    addError({
      type: error.errorType,
      message: getErrorMessage(error.errorType, error.message),
      details: error.message,
      dismissable: true,
    });
  }
}

/**
 * Create an AbortController with timeout
 */
function createTimeoutController(timeout: number): { controller: AbortController; clear: () => void } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return {
    controller,
    clear: () => clearTimeout(timeoutId),
  };
}

/**
 * Base fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  { showToast = true, timeout = DEFAULT_TIMEOUT }: { showToast?: boolean; timeout?: number } = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const { controller, clear } = createTimeoutController(timeout);

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',  // Include cookies for session
      signal: controller.signal,
    });

    clear();

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { error: response.statusText };
      }

      const errorType = getErrorType(response.status);
      const error = new ApiError(
        errorData.error || `HTTP ${response.status}`,
        response.status,
        errorType,
        errorData
      );

      handleGlobalError(error, showToast);
      throw error;
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) return {} as T;

    return JSON.parse(text);
  } catch (error) {
    clear();

    // Handle network errors and timeouts
    if (error instanceof ApiError) {
      throw error;
    }

    const err = error as Error;
    let apiError: ApiError;

    if (err.name === 'AbortError') {
      apiError = new ApiError('Zahtev je istekao', 408, 'timeout');
    } else if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
      apiError = new ApiError('Server nije dostupan', 0, 'network');
    } else {
      apiError = new ApiError(err.message || 'Nepoznata greška', 0, 'unknown');
    }

    handleGlobalError(apiError, showToast);
    throw apiError;
  }
}

/**
 * GET request
 */
export async function get<T>(endpoint: string): Promise<T> {
  return apiFetch<T>(endpoint, { method: 'GET' });
}

/**
 * POST request
 */
export async function post<T>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

/**
 * PUT request
 */
export async function put<T>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request
 */
export async function del<T>(endpoint: string): Promise<T> {
  return apiFetch<T>(endpoint, { method: 'DELETE' });
}

/**
 * PATCH request
 */
export async function patch<T>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * SSE (Server-Sent Events) connection for streaming chat
 *
 * @param endpoint - API endpoint
 * @param data - POST data
 * @param onEvent - Callback for each SSE event
 * @param onError - Callback for errors
 * @returns Cleanup function to abort the connection
 */
export function streamChat(
  endpoint: string,
  data: Record<string, unknown>,
  onEvent: (event: { type: string; data: string }) => void,
  onError: (error: ApiError) => void
): () => void {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT);

  (async () => {
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify(data),
        credentials: 'include',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: response.statusText };
        }

        const errorType = getErrorType(response.status);
        const error = new ApiError(
          errorData.error || `HTTP ${response.status}`,
          response.status,
          errorType,
          errorData
        );

        // Show global error for auth failures
        if (errorType === 'auth') {
          handleGlobalError(error, true);
        }

        throw error;
      }

      const reader = response.body?.getReader();
      if (!reader) throw new ApiError('No response body', 0, 'unknown');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            // Event type line, next line should be data
            continue;
          }
          if (line.startsWith('data: ')) {
            const eventData = line.slice(6);
            // Try to parse as JSON, otherwise treat as content
            try {
              const parsed = JSON.parse(eventData);
              onEvent({ type: parsed.type || 'content', data: eventData });
            } catch {
              onEvent({ type: 'content', data: eventData });
            }
          }
        }
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if ((error as Error).name === 'AbortError') {
        // Check if it was a timeout or manual abort
        const timeoutError = new ApiError(
          'AI odgovor je trajao predugo. Pokušaj ponovo.',
          408,
          'llm'
        );
        onError(timeoutError);
        return;
      }

      if (error instanceof ApiError) {
        onError(error);
        return;
      }

      const err = error as Error;
      let apiError: ApiError;

      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        apiError = new ApiError('Server nije dostupan', 0, 'network');
        handleGlobalError(apiError, true);
      } else {
        apiError = new ApiError(err.message || 'Nepoznata greška', 0, 'unknown');
      }

      onError(apiError);
    }
  })();

  return () => {
    clearTimeout(timeoutId);
    controller.abort();
  };
}

export default {
  get,
  post,
  put,
  del,
  patch,
  streamChat,
};
